import React, { useState } from 'react';
import { Card, Row, Col, Button, Form, Spinner } from 'react-bootstrap';
import { FaClipboardList, FaSave, FaTimes } from 'react-icons/fa';
import { SecureImage } from './SecureMedia';
import { API_BASE } from '../config';

export default function AnalysisInbox({
  items,
  onItemsChange,
  onSaveComplete,
  onTaskDelete,
  taskId,
  onTaskIdChange,
  roomList,
  containerList,
  t,
  currency,
  authenticatedFetch,
}) {
  const [saving, setSaving] = useState(false);

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    onItemsChange(updated);
  };

  const handleRemove = (index) => onItemsChange(items.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await authenticatedFetch(`${API_BASE}/inventory/add`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(items),
      });
      if (res.ok) {
        await onSaveComplete();
        if (taskId) { await onTaskDelete(taskId); onTaskIdChange(null); }
        onItemsChange([]);
        alert(t.itemsAddedSuccess);
      }
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handleDiscard = () => {
    if (taskId) onTaskDelete(taskId);
    else onItemsChange([]);
  };

  if (items.length === 0) return null;

  return (
    <div className="mb-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 text-success"><FaClipboardList className="me-2" />{t.justFound}</h4>
        <div>
          <Button variant="success" size="sm" className="me-2" onClick={handleSave} disabled={saving}>
            {saving
              ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
              : <FaSave className="me-2" />}
            {saving ? t.saving : t.confirmSave}
          </Button>
          <Button variant="outline-danger" size="sm" onClick={handleDiscard} disabled={saving}>
            <FaTimes className="me-2" />{t.discardAll}
          </Button>
        </div>
      </div>

      <div className="d-flex flex-column gap-3">
        {items.map((item, idx) => (
          <Card key={idx} className="shadow-sm border-2 border-success-subtle overflow-hidden">
            <Card.Body className="p-3">
              <Row className="g-3">
                <Col xs={12} className="d-flex gap-3 align-items-start border-bottom pb-2 mb-2">
                  <div style={{ width: '70px', height: '70px', flexShrink: 0, borderRadius: '8px', overflow: 'hidden', background: '#f8f9fa', border: '1px solid #dee2e6' }}>
                    <SecureImage
                      src={item.thumbnail_file ? `${API_BASE}/${item.thumbnail_file}` : `${API_BASE}/media/${item.source_file?.split('/').pop()}`}
                      alt="preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                  <div className="flex-grow-1">
                    <Form.Group className="mb-1">
                      <Form.Label className="small fw-bold text-muted mb-0">{t.item}</Form.Label>
                      <Form.Control size="sm" type="text" className="fw-bold border-0 bg-transparent p-0" style={{ fontSize: '1rem' }} value={item.item} onChange={(e) => handleItemChange(idx, 'item', e.target.value)} />
                    </Form.Group>
                    <div className="d-flex gap-3">
                      <div className="flex-grow-1">
                        <Form.Label className="small fw-bold text-muted mb-0">{t.cost} ({currency})</Form.Label>
                        <Form.Control size="sm" type="number" className="text-success fw-bold" value={item.cost_cad} onChange={(e) => handleItemChange(idx, 'cost_cad', e.target.value)} />
                      </div>
                      <div style={{ width: '70px' }}>
                        <Form.Label className="small fw-bold text-muted mb-0">{t.qty}</Form.Label>
                        <Form.Control size="sm" type="number" value={item.quantity ?? ''} onChange={(e) => handleItemChange(idx, 'quantity', e.target.value)} />
                      </div>
                    </div>
                  </div>
                  <Button variant="link" className="text-danger p-0 ms-2" onClick={() => handleRemove(idx)}><FaTimes size={20} /></Button>
                </Col>
                <Col xs={12}>
                  <Row className="g-2">
                    <Col xs={12} sm={4}>
                      <Form.Label className="small fw-bold text-muted mb-0">{t.type}</Form.Label>
                      <Form.Control size="sm" type="text" placeholder={t.categoryPlaceholder} value={item.type} onChange={(e) => handleItemChange(idx, 'type', e.target.value)} />
                    </Col>
                    <Col xs={6} sm={4}>
                      <Form.Label className="small fw-bold text-muted mb-0">{t.room}</Form.Label>
                      <Form.Select size="sm" value={item.room || ''} onChange={(e) => handleItemChange(idx, 'room', e.target.value)}>
                        <option value="">{t.selectRoom}</option>
                        {roomList.map((r, i) => <option key={i} value={r}>{r}</option>)}
                      </Form.Select>
                    </Col>
                    <Col xs={6} sm={4}>
                      <Form.Label className="small fw-bold text-muted mb-0">{t.storage}</Form.Label>
                      <Form.Select size="sm" value={item.container || ''} onChange={(e) => handleItemChange(idx, 'container', e.target.value)}>
                        <option value="">{t.selectStorage}</option>
                        {containerList.map((c, i) => <option key={i} value={c}>{c}</option>)}
                      </Form.Select>
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small fw-bold text-muted mb-0">{t.cond}</Form.Label>
                      <Form.Select size="sm" value={item.condition} onChange={(e) => handleItemChange(idx, 'condition', e.target.value)}>
                        <option>New</option><option>Excellent</option><option>Good</option><option>Fair</option>
                      </Form.Select>
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small fw-bold text-muted mb-0">{t.yearPurchased}</Form.Label>
                      <Form.Control size="sm" type="number" placeholder="Year" value={item.year_purchased || ''} onChange={(e) => handleItemChange(idx, 'year_purchased', e.target.value)} />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))}
      </div>
    </div>
  );
}
